import React from 'react';
import { t } from 'i18next';
import { Tooltip } from 'primereact/tooltip';
import { getMonitorTypeIcon } from '../../../prompt/preview/components/stats/helpers/StatsHelper';
import { MonitorType } from '../../../../shared/types/monitor/MonitorType';
import { MODEL_ICON_TO_I18N_KEY } from '../../../../shared/components/model-tooltips/ModelTooltips';

interface ModelIconsListProps {
  /** MonitorType values from the backend (e.g. 'GPT_API_BROWSER', 'PERPLEXITY_SEARCH_BROWSER'). */
  models: string[];
  /** Max icons to render inline before collapsing into '+N'. */
  maxVisible?: number;
  /** Stable id segment used to scope the overflow Tooltip to a single row. */
  tooltipKey?: string;
}

/**
 * Deduplicates `models` by their resolved icon class (multiple MonitorTypes often map to
 * the same icon, e.g. GPT vs GPT_API_BROWSER both become 'ico--gpt-bck') and renders each
 * unique icon once. Overflow collapses into a `+N` chip with a tooltip listing the hidden
 * model labels — same UX pattern as the `sourceDomains` column on ChatsForDomainTable.
 */
const ModelIconsList: React.FC<ModelIconsListProps> = ({
  models,
  maxVisible = 4,
  tooltipKey = 'row',
}) => {
  const uniqueIcons = Array.from(
    new Set(
      models
        .map((m) => getMonitorTypeIcon(m as MonitorType))
        .filter((icon): icon is NonNullable<typeof icon> => icon !== null)
    )
  );

  const visible = uniqueIcons.slice(0, maxVisible);
  const hidden = uniqueIcons.slice(maxVisible);
  const overflow = hidden.length;
  // Sanitise tooltipKey for use as a CSS id — incoming values are often URLs and contain
  // characters PrimeReact's Tooltip selector chokes on (`:`, `/`, `.`, `?`, etc.).
  const safeKey = tooltipKey.replace(/[^a-zA-Z0-9_-]/g, '-');
  const overflowId = `pm-models-overflow-${safeKey}`;
  const tooltipText = hidden
    .map((icon) => {
      const key = MODEL_ICON_TO_I18N_KEY.get(icon);
      return key ? t(key) : icon;
    })
    .join('\n');

  return (
    <div className="model-icons-list">
      {visible.map((iconClass) => (
        <span key={iconClass} className={`${iconClass} ico--sm`} />
      ))}
      {overflow > 0 && (
        <>
          <span
            className="model-icons-list__overflow"
            data-pr-tooltip={tooltipText}
            data-pr-position="top"
            id={overflowId}
          >
            +{overflow}
          </span>
          <Tooltip target={`#${overflowId}`} />
        </>
      )}
    </div>
  );
};

export default ModelIconsList;
